import { Module } from 'vuex';
import userService, { User, CreateUserData, UpdateUserData } from '@/services/user.service';

export interface UsersState {
  users: User[];
  currentUser: User | null;
  loading: boolean;
  error: string | null;
}

const usersModule: Module<UsersState, any> = {
  namespaced: true,

  state: (): UsersState => ({
    users: [],
    currentUser: null,
    loading: false,
    error: null,
  }),

  mutations: {
    SET_USERS(state, users: User[]) {
      state.users = users;
    },

    SET_CURRENT_USER(state, user: User | null) {
      state.currentUser = user;
    },

    ADD_USER(state, user: User) {
      state.users.push(user);
    },

    UPDATE_USER(state, updatedUser: User) {
      const index = state.users.findIndex(u => u.id === updatedUser.id);
      if (index !== -1) {
        state.users.splice(index, 1, updatedUser);
      }
    },

    REMOVE_USER(state, userId: string) {
      state.users = state.users.filter(u => u.id !== userId);
    },

    SET_LOADING(state, loading: boolean) {
      state.loading = loading;
    },

    SET_ERROR(state, error: string | null) {
      state.error = error;
    },
  },

  actions: {
    /**
     * Obtener todos los usuarios
     */
    async fetchUsers({ commit }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const users = await userService.getAllUsers();
        commit('SET_USERS', users);
        return users;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Error al cargar usuarios';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Obtener usuario por ID
     */
    async fetchUserById({ commit }, userId: string) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const user = await userService.getUserById(userId);
        commit('SET_CURRENT_USER', user);
        return user;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Error al cargar usuario';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Crear nuevo usuario
     */
    async createUser({ commit }, userData: CreateUserData) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const newUser = await userService.createUser(userData);
        commit('ADD_USER', newUser);
        return newUser;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Error al crear usuario';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Actualizar usuario
     */
    async updateUser({ commit }, { userId, userData }: { userId: string; userData: UpdateUserData }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const updatedUser = await userService.updateUser(userId, userData);
        commit('UPDATE_USER', updatedUser);
        return updatedUser;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Error al actualizar usuario';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Eliminar usuario
     */
    async deleteUser({ commit }, userId: string) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        await userService.deleteUser(userId);
        commit('REMOVE_USER', userId);
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Error al eliminar usuario';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Cambiar contraseña de usuario
     */
    async changeUserPassword({ commit }, { userId, newPassword }: { userId: string; newPassword: string }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const result = await userService.changeUserPassword(userId, newPassword);
        return result;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Error al cambiar contraseña';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Activar/Desactivar usuario
     */
    async toggleUserStatus({ commit }, { userId, status }: { userId: string; status: boolean }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const result = await userService.toggleUserStatus(userId, status);
        
        // Actualizar el estado del usuario en el store
        const user = await userService.getUserById(userId);
        commit('UPDATE_USER', user);
        
        return result;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Error al cambiar estado del usuario';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Limpiar error
     */
    clearError({ commit }) {
      commit('SET_ERROR', null);
    },
  },

  getters: {
    allUsers: (state) => state.users,
    currentUser: (state) => state.currentUser,
    isLoading: (state) => state.loading,
    error: (state) => state.error,
    activeUsers: (state) => state.users.filter(u => u.status),
    inactiveUsers: (state) => state.users.filter(u => !u.status),
    userById: (state) => (userId: string) => state.users.find(u => u.id === userId),
  },
};

export default usersModule;
